import { CartItem } from './cart'
import { CustomerInfo, DeliveryInfo, PickupInfo, DineInInfo } from './customer'

export type OrderType = 'delivery' | 'pickup' | 'dine-in'

export type DeliveryPlatform = 'ubereats' | 'doordash'

export type OrderStatus =
  | 'idle'
  | 'pending'
  | 'validating'
  | 'submitting'
  | 'confirmed'
  | 'preparing'
  | 'ready'
  | 'out-for-delivery'
  | 'delivered'
  | 'completed'
  | 'cancelled'
  | 'error'

export type PaymentMethod =
  | 'cash'
  | 'card-on-delivery'
  | 'card-online'
  | 'digital-wallet'

export interface PaymentInfo {
  method: PaymentMethod
  cardLast4?: string
  transactionId?: string
}

export interface OrderTotals {
  subtotal: number
  discount?: number
  total: number
}

export interface Order {
  id: string
  orderNumber: string
  orderType: OrderType
  items: CartItem[]
  customer: CustomerInfo
  delivery?: DeliveryInfo
  pickup?: PickupInfo
  dineIn?: DineInInfo
  payment: PaymentInfo
  totals: OrderTotals
  status: OrderStatus
  createdAt: string
  updatedAt?: string
  estimatedTime?: string
  specialInstructions?: string
  restaurantId: string
}

export interface OrderValidationError {
  field: string
  message: string
}

export interface OrderSubmitRequest {
  orderType: OrderType
  items: CartItem[]
  customer: CustomerInfo
  delivery?: DeliveryInfo
  pickup?: PickupInfo
  dineIn?: DineInInfo
  payment: PaymentInfo
  totals: OrderTotals
  specialInstructions?: string
  restaurantId: string
}

export interface OrderSubmitResponse {
  success: boolean
  order?: Order
  errors?: OrderValidationError[]
  message?: string
}

/**
 * Order tracking status for the customer-facing tracking page
 * These represent the visual steps shown to customers
 */
export type TrackingStatus =
  | 'confirmed'
  | 'preparing'
  | 'ready'
  | 'out-for-delivery'
  | 'delivered'
  | 'completed'

/**
 * Configuration for each tracking step
 */
export interface TrackingStep {
  id: TrackingStatus
  label: string
  description: string
}

/**
 * Get tracking steps based on order type
 */
export function getTrackingSteps(orderType: OrderType): TrackingStep[] {
  const baseSteps: TrackingStep[] = [
    {
      id: 'confirmed',
      label: 'Order Confirmed',
      description: 'Your order has been received',
    },
    {
      id: 'preparing',
      label: 'Preparing',
      description: 'Your order is being prepared',
    },
  ]

  if (orderType === 'delivery') {
    return [
      ...baseSteps,
      {
        id: 'out-for-delivery',
        label: 'Out for Delivery',
        description: 'Your order is on its way',
      },
      {
        id: 'delivered',
        label: 'Delivered',
        description: 'Your order has arrived',
      },
    ]
  }

  // Pickup or dine-in
  return [
    ...baseSteps,
    {
      id: 'ready',
      label: 'Ready for Pickup',
      description: 'Your order is ready',
    },
    {
      id: 'completed',
      label: 'Picked Up',
      description: 'Order complete',
    },
  ]
}

/**
 * Get the index of the current tracking step
 */
export function getTrackingStepIndex(
  status: OrderStatus,
  orderType: OrderType
): number {
  const steps = getTrackingSteps(orderType)
  const statusToStepMap: Record<OrderStatus, TrackingStatus | null> = {
    idle: null,
    pending: 'confirmed',
    validating: null,
    submitting: null,
    confirmed: 'confirmed',
    preparing: 'preparing',
    ready: 'ready',
    'out-for-delivery': 'out-for-delivery',
    delivered: 'delivered',
    completed: 'completed',
    cancelled: null,
    error: null,
  }

  const mappedStatus = statusToStepMap[status]
  if (!mappedStatus) return -1

  return steps.findIndex((step) => step.id === mappedStatus)
}
